import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context";
import { Chapter, Reveal } from "@/components/Reveal";

const ABOUT_IMG = "/photos/merce-portrait.png";

export default function About() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="about" ref={ref} data-testid="about-section" className="py-28 sm:py-36 bg-cream/60 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        <Chapter number={t.about.chapter} label={t.about.label} />
        <div className="grid grid-cols-12 gap-y-10 lg:gap-16 items-start">
          <div className="col-span-12 lg:col-span-5 relative">
            <Reveal>
              <div className="relative max-w-md">
                <div className="absolute -bottom-5 -left-5 w-24 h-24 border-b border-l border-gold/60" aria-hidden="true" />
                <div className="overflow-hidden">
                  <motion.img
                    data-testid="about-portrait"
                    src={ABOUT_IMG}
                    alt={t.about.portraitAlt}
                    style={{ y: imgY }}
                    className="w-full aspect-[3/4] object-cover img-editorial scale-110 will-change-transform"
                  />
                </div>
              </div>
            </Reveal>
          </div>

          <div className="col-span-12 lg:col-span-7 lg:pt-6">
            <Reveal>
              <h2
                data-testid="about-title"
                className="font-serif text-4xl sm:text-5xl lg:text-6xl text-charcoal font-medium leading-[1.1]"
              >
                {t.about.title}
              </h2>
            </Reveal>

            {t.about.paragraphs.map((p, i) => (
              <Reveal key={i} delay={0.15 + i * 0.1}>
                <p className="mt-7 max-w-xl text-base sm:text-lg text-ink/75 leading-relaxed font-light">{p}</p>
              </Reveal>
            ))}

            <Reveal delay={0.35}>
              <blockquote
                data-testid="about-quote"
                className="mt-12 border-l-2 border-gold pl-6 sm:pl-8 font-serif italic text-2xl sm:text-3xl text-charcoal/90 leading-snug max-w-lg"
              >
                “{t.about.quote}”
              </blockquote>
            </Reveal>

            <Reveal delay={0.45}>
              <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2">
                {t.about.values.map((v) => (
                  <span key={v} className="text-[10px] sm:text-[11px] uppercase tracking-[0.26em] text-taupe">
                    {v}
                  </span>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.55}>
              <p className="mt-12 font-serif italic text-3xl text-charcoal/80">{t.about.signature}</p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
